import { Ohje } from "./Ohje";
import { useLataus, LatausTila } from "./Lataus";
import { useCallback, useEffect, useState } from "react";
import { useKohde } from "./Kohdekonteksti";
import { api, ApiError, type Kayttaja, type Kohde, type SaapunutViesti, type LahetettyViesti } from "./api";
import { naytaPvm } from "./pvm";
import { Tilasana } from "./Tila";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Viestit (162, Toni 21.–23.9.2026).

   Yksisuuntainen tiedotus: ei vastauksia. Lukija merkitsee luetuksi,
   lähettäjä näkee henkilöittäin kuka on lukenut. Vastaanottajat
   lukitaan lähetyshetkellä, eikä lähetettyä viestiä muokata —
   peruutus on merkintä, ja vanhentunut viesti poistuu vastaanottajan
   näkyvistä mutta säilyy lähettäjän historiassa.
   ═══════════════════════════════════════════════════════════ */

export default function Viestit({ kayttaja, luettuMuuttui }: {
  kayttaja: Kayttaja; luettuMuuttui: () => void;
}) {
  const saaLahettaa = kayttaja.oikeudet.includes("viestin_lahetys");
  const [saapuneet, setSaapuneet] = useState<SaapunutViesti[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  const hae = useCallback(async () => {
    try { setSaapuneet(await api.viestit()); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vie.haku_virhe")); }
  }, []);
  useEffect(() => { void hae(); }, [hae]);

  async function merkitse(id: number) {
    try { await api.merkitseLuetuksi(id); await hae(); luettuMuuttui(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vie.merkinta_virhe")); }
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("valikko.viestit")}</div>
        <div className="head__rivi"><h1>{t("vie.tiedotteet")}</h1></div>
      </header>

      {/* Ohje ylös (Toni 24.9.2026). Lähde: /api/viestit*: vastaanotto vaadi (oma rivi), lähetys ja peruutus oikeus viestin_lahetys (162). */}
      <Ohje
        kuka={<>{t("vie.ohje.kuka")}</>}
        luvut={<>{t("vie.ohje.luvut")}</>} />
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      <section className="panel" data-saapuneet={saapuneet?.length ?? ""}>
        <h3>{t("vie.saapuneet")}</h3>
        {saapuneet === null && <p className="dim">{t("yleinen.ladataan")}</p>}
        {saapuneet?.length === 0 && <p className="dim">{t("vie.ei_saapuneita")}</p>}
        {saapuneet && saapuneet.length > 0 && (
          <ul className="viestit">
            {saapuneet.map((v) => (
              <li key={v.id} className={"viesti" + (v.luettu ? "" : " viesti--lukematon")} data-viesti={v.id}>
                <div className="viesti__yla">
                  <strong>{v.otsikko}</strong>
                  <span className="dim"> · {v.lahettaja} · {naytaPvm(v.lahetetty.slice(0, 10))}
                    {v.voimassa_asti && <> · {t("vie.voimassa_asti", { pvm: naytaPvm(v.voimassa_asti) ?? "" })}</>}</span>
                  {v.luettu
                    ? <Tilasana laji="kunnossa">{t("vie.luettu_pvm", { pvm: naytaPvm(v.luettu.slice(0, 10)) ?? "" })}</Tilasana>
                    : <button className="nappi nappi--kevyt" onClick={() => void merkitse(v.id)}>{t("vie.merkitse_luetuksi")}</button>}
                </div>
                <div className="viesti__teksti">{v.teksti}</div>
                {/* 227: linkki on näkymän osoite; vain sovelluksen sisäinen #/ (kannan rajoite), oikeudet tarkistetaan avattaessa. */}
                {v.linkki?.startsWith("#/") && (
                  <a className="viesti__linkki" href={v.linkki} data-viesti-linkki={v.linkki}>{v.linkki_teksti ?? v.linkki}</a>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="ikkuna__vihje">{t("vie.ei_vastata")}</p>
      </section>

      {saaLahettaa ? <Lahetys kayttaja={kayttaja} /> : (
        /* Ei lähetysnappia → ei lupausta lähettämisestä, vaan kuka lähettää (24.9.2026). */
        <p className="dim" data-lahetys="ei-oikeutta">
          {t("vie.ei_oikeutta")}
        </p>
      )}
    </>
  );
}

/* ── Lähetys ja lähetetyt: vain oikeudella `viestin_lahetys` ── */
function Lahetys({ kayttaja }: { kayttaja: Kayttaja }) {
  const rajaamaton = kayttaja.kohteet.length === 0;
  const [kohdistus, setKohdistus] = useState<"kaikki" | "kayttajat" | "kohteen_tyonjohto">(
    rajaamaton ? "kaikki" : "kohteen_tyonjohto");
  const [otsikko, setOtsikko] = useState("");
  const [teksti, setTeksti] = useState("");
  const [voimassa, setVoimassa] = useState("");
  const [valitut, setValitut] = useState<number[]>([]);
  /** Nimenomainen «kaikki kohteen työnjohtajat» — tyhjä valinta ei tarkoita kaikkia (Toni 23.9.). */
  const [kaikkiTj, setKaikkiTj] = useState(false);
  const [kohdeId, setKohdeId] = useState<number | "">("");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => { if (typeof ylaKohde === "number" && kohdeId === "") setKohdeId(ylaKohde); }, [ylaKohde]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [ehdokkaat, setEhdokkaat] = useState<{ id: number; nimi: string; roolit?: string | null }[]>([]);
  const [lahetetyt, setLahetetyt] = useState<LahetettyViesti[] | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  const haeLahetetyt = useCallback(async () => {
    try { setLahetetyt(await api.lahetetytViestit()); } catch { setLahetetyt([]); }
  }, []);
  const klKohteet = useLataus();
  useEffect(() => { void haeLahetetyt(); klKohteet.seuraa(api.kohteet().then(setKohteet)); }, [haeLahetetyt, klKohteet.kerta]);

  // Vastaanottajaehdokkaat vaihtuvat kohdistuksen mukaan; valinta nollataan,
  // ettei edellisen kohdistuksen valinta lähde mukaan huomaamatta.
  useEffect(() => {
    setValitut([]); setKaikkiTj(false); setEhdokkaat([]);
    // Päätös 99: edellisen kohteen myöhästyneet ehdokkaat eivät saa korvata nykyisen kohteen listaa.
    let elossa = true;
    if (kohdistus === "kayttajat") api.viestinKayttajat().then((e) => { if (elossa) setEhdokkaat(e); }).catch(() => {});
    if (kohdistus === "kohteen_tyonjohto" && kohdeId !== "")
      api.kohteenTyonjohtajat(kohdeId).then((e) => { if (elossa) setEhdokkaat(e); }).catch(() => {});
    return () => { elossa = false; };
  }, [kohdistus, kohdeId]);

  async function laheta() {
    // Nimetty syy myös selaimessa; palvelin tekee saman tarkistuksen (lomake ei ole portti).
    if (kohdistus !== "kaikki" && valitut.length === 0 && !kaikkiTj) {
      setIlmoitus(t("vie.ei_vastaanottajia"));
      return;
    }
    setKesken(true); setIlmoitus(null);
    try {
      const r = await api.lahetaViesti({
        otsikko, teksti, kohdistus,
        ...(kohdistus !== "kaikki" && valitut.length > 0 ? { kayttaja_idt: valitut } : {}),
        ...(kohdistus === "kohteen_tyonjohto" && kaikkiTj ? { kaikki_tyonjohtajat: true } : {}),
        ...(kohdistus === "kohteen_tyonjohto" && kohdeId !== "" ? { kohde_id: kohdeId } : {}),
        ...(voimassa ? { voimassa_asti: voimassa } : {}),
      });
      setIlmoitus(t("vie.lahetetty_n", { n: r.vastaanottajia }));
      setOtsikko(""); setTeksti(""); setVoimassa(""); setValitut([]); setKaikkiTj(false);
      await haeLahetetyt();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vie.lahetys_virhe"));
    } finally { setKesken(false); }
  }

  async function peru(id: number) {
    const peruste = window.prompt(t("vie.peru_kysymys"));
    if (!peruste || peruste.trim().length < 3) return;
    try { await api.peruViesti(id, peruste.trim()); await haeLahetetyt(); }
    catch (e) { setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("vie.peruutus_virhe")); }
  }

  // Vastaanottajavalinnan puute EI estä nappia: painallus kertoo nimetyn syyn.
  const valintaPuuttuu = kohdistus === "kohteen_tyonjohto" && kohdeId === "";

  return (
    <>
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />
      <section className="panel" data-viestilomake="">
        <h3>{t("vie.laheta_tiedote")}</h3>
        <div className="allokointi viestilomake__rivi">
          <label>{t("vie.kenelle")}
            <select value={kohdistus} onChange={(e) => setKohdistus(e.target.value as typeof kohdistus)} data-viesti-kohdistus="">
              {rajaamaton && <option value="kaikki">{t("vie.kaikille")}</option>}
              {rajaamaton && <option value="kayttajat">{t("vie.valituille")}</option>}
              <option value="kohteen_tyonjohto">{t("vie.kohteen_tyonjohdolle")}</option>
            </select>
          </label>
          {kohdistus === "kohteen_tyonjohto" && (
            <label>{t("kuittaus.kohde")}
              <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value ? Number(e.target.value) : "")} data-kohdekoukku="viestit">
                <option value="">{t("hl.valitse")}</option>
                {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
              </select>
            </label>
          )}
          <label>{t("vie.voimassa_kentta")}
            <input type="date" value={voimassa} onChange={(e) => setVoimassa(e.target.value)} />
          </label>
        </div>
        {kohdistus !== "kaikki" && ehdokkaat.length > 0 && (
          <div className="viesti__ehdokkaat" data-ehdokkaat={ehdokkaat.length}>
            <span className="dim">
              {kohdistus === "kohteen_tyonjohto"
                ? t("vie.valitse_tyonjohtajat")
                : t("vie.valitse_vastaanottajat")}
            </span>
            {kohdistus === "kohteen_tyonjohto" && (
              <label className="valinta" data-kaikki-tyonjohtajat="">
                <input type="checkbox" checked={kaikkiTj}
                       onChange={(e) => { setKaikkiTj(e.target.checked); if (e.target.checked) setValitut([]); }} />
                <strong>{t("vie.kaikki_tyonjohtajat", { n: ehdokkaat.length })}</strong>
              </label>
            )}
            {ehdokkaat.map((h) => (
              <label key={h.id} className="valinta">
                <input type="checkbox" checked={valitut.includes(h.id)} disabled={kaikkiTj}
                       onChange={(e) => setValitut(e.target.checked ? [...valitut, h.id] : valitut.filter((x) => x !== h.id))} />
                {h.nimi}{h.roolit ? <span className="dim"> · {h.roolit}</span> : null}
              </label>
            ))}
          </div>
        )}
        {kohdistus === "kohteen_tyonjohto" && kohdeId !== "" && ehdokkaat.length === 0 && (
          <p className="huom">{t("vie.ei_tyonjohtajia")}</p>
        )}
        {/* T485 (Toni 11.10.2026): otsikko kentän yläpuolella (yhteinen label-sääntö; inline display:block ohitti sen),
            kentät lomakkeen levyisiä ja 14 px:n välein, viestissä vähintään viisi riviä. */}
        <div className="viestilomake">
          <label>{t("vie.otsikko")}
            <input value={otsikko} maxLength={200} onChange={(e) => setOtsikko(e.target.value)} data-kentta="viestin otsikko" />
          </label>
          <label>{t("vie.viesti")}
            <textarea rows={6} value={teksti} maxLength={5000} onChange={(e) => setTeksti(e.target.value)} data-kentta="viestin teksti" />
          </label>
        </div>
        <div className="viestilomake__toiminnot">
          <button className="nappi nappi--paa"
                  disabled={kesken || !otsikko.trim() || !teksti.trim() || valintaPuuttuu}
                  onClick={() => void laheta()}>{t("vie.laheta")}</button>
          {!kesken && (valintaPuuttuu || !otsikko.trim() || !teksti.trim()) && (
            <span className="este-syy" data-este-syy="">
              {valintaPuuttuu ? t("vie.syy_kohde") : !otsikko.trim() && !teksti.trim() ? t("vie.syy_otsikko_viesti")
                : !otsikko.trim() ? t("vie.syy_otsikko") : t("vie.syy_viesti")}
            </span>
          )}
        </div>
        {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
        <p className="ikkuna__vihje">
          {t("vie.lukitaan_vihje")}
        </p>
      </section>

      <section className="panel" data-lahetetyt={lahetetyt?.length ?? ""}>
        <h3>{t("vie.lahetetyt")}</h3>
        {lahetetyt === null && <p className="dim">{t("yleinen.ladataan")}</p>}
        {lahetetyt?.length === 0 && <p className="dim">{t("vie.ei_lahetettyja")}</p>}
        {lahetetyt && lahetetyt.length > 0 && (
          <ul className="viestit">
            {lahetetyt.map((v) => {
              const luettu = v.vastaanottajat.filter((x) => x.luettu).length;
              return (
                <li key={v.id} className="viesti" data-lahetetty={v.id}>
                  <div className="viesti__yla">
                    <strong>{v.otsikko}</strong>
                    <span className="dim"> · {naytaPvm(v.lahetetty.slice(0, 10))} · {v.lahettaja}
                      {v.kohde && <> · {v.kohde}</>}</span>
                    <Tilasana laji={luettu === v.vastaanottajat.length ? "kunnossa" : "varoitus"}>
                      {t("vie.luettu_x_y", { x: luettu, y: v.vastaanottajat.length })}
                    </Tilasana>
                    {v.peruttu && <Tilasana laji="ei_mitattu">{t("vie.peruttu")} · {v.peruste}</Tilasana>}
                    {!v.peruttu && v.vanhentunut && <Tilasana laji="ei_mitattu">{t("vie.vanhentunut")}</Tilasana>}
                    {!v.peruttu && (
                      <button className="nappi nappi--kevyt" onClick={() => void peru(v.id)}>{t("vie.peru")}</button>
                    )}
                  </div>
                  <details>
                    <summary className="dim">{t("vie.vastaanottajat")}</summary>
                    <ul className="tarvevaatimukset">
                      {v.vastaanottajat.map((x, i) => (
                        <li key={i}>{x.nimi} · {x.luettu
                          ? <span>{t("vie.luettu_pvm", { pvm: naytaPvm(x.luettu.slice(0, 10)) ?? "" })}</span>
                          : <span className="dim">{t("vie.lukematta")}</span>}</li>
                      ))}
                    </ul>
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
